import React, { useState, useEffect } from 'react';
import { 
  UserCheck, Shield, Users, Settings, Plus, Edit2, Trash2, Search, 
  Eye, Copy, Crown, Building, GraduationCap, BookOpen, FileText, 
  User, Star, Award, Badge, Key, Lock, Unlock, Palette, Hash
} from 'lucide-react';
import {
  getAllFuncoes,
  createFuncao,
  updateFuncao,
  deleteFuncao,
  toggleFuncaoStatus,
  searchFuncoes,
  getFuncoesStats,
  getUsersByFuncao,
  duplicateFuncao,
  getPermissoesDisponiveis,
  getIconesDisponiveis
} from '../services/funcoesUsuariosService';
import { getCurrentUserProfile } from '../services/usuariosService';
import '../styles/funcoes-usuarios.css';

const FuncoesUsuarios = () => {
  const [funcoes, setFuncoes] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, system: 0, custom: 0, total_users: 0 });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [editingFuncao, setEditingFuncao] = useState(null);
  const [selectedFuncaoUsers, setSelectedFuncaoUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    cor: '#3b82f6',
    icone: 'Shield',
    nivel_acesso: 1,
    permissoes: [],
    is_active: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);

  // Mapear ícones para componentes
  const iconMap = {
    Shield, Crown, Building, GraduationCap, BookOpen, FileText, 
    Eye, User, Users, UserCheck, Settings, Star, Award, Badge, Key, Lock, Unlock
  };

  // Carregar dados iniciais
  useEffect(() => {
    loadInitialData();
  }, []);

  // Recarregar dados quando o perfil do usuário for carregado
  useEffect(() => {
    if (currentUserProfile) {
      loadData();
    }
  }, [currentUserProfile]);

  const loadInitialData = async () => {
    try {
      // Carregar perfil do usuário
      const profileResult = await getCurrentUserProfile();
      if (profileResult.success) {
        setCurrentUserProfile(profileResult.data);
      }
      
      await loadData();
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
    }
  };

  // Buscar com filtro
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [funcoesResult, statsResult] = await Promise.all([
        getAllFuncoes(),
        getFuncoesStats()
      ]);

      if (funcoesResult.success) {
        // Aplicar contagem filtrada por escola para cada função
        console.log('🔍 Aplicando filtro school_adm:', currentUserProfile?.school_adm, 'school_id:', currentUserProfile?.school_id);
        
        const funcoesComContagem = await Promise.all(
          funcoesResult.data.map(async (funcao) => {
            let usuariosCount = funcao.usuarios_count || 0;
            
            if (currentUserProfile?.school_adm && currentUserProfile?.school_id) {
              // Para school admin, contar apenas usuários da sua escola
              console.log('📊 Contando usuários da função:', funcao.nome, 'ID:', funcao.id);
              const result = await getUsersByFuncao(funcao.id);
              usuariosCount = result.success ? result.data.length : 0;
              console.log('📊 Resultado para função', funcao.nome, ':', usuariosCount, 'usuários');
            }
            
            return {
              ...funcao,
              usuarios_count: usuariosCount
            };
          })
        );
        
        setFuncoes(funcoesComContagem);
      }

      if (statsResult.success) {
        setStats(statsResult.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      const result = await searchFuncoes(searchTerm);
      if (result.success) {
        setFuncoes(result.data);
      }
    } catch (error) {
      console.error('Erro na busca:', error);
    }
  };

  const openModal = (funcao = null) => {
    if (funcao) {
      setEditingFuncao(funcao);
      setFormData({
        nome: funcao.nome || '',
        descricao: funcao.descricao || '',
        cor: funcao.cor || '#3b82f6',
        icone: funcao.icone || 'Shield',
        nivel_acesso: funcao.nivel_acesso || 1,
        permissoes: funcao.permissoes || [],
        is_active: funcao.is_active
      });
    } else {
      setEditingFuncao(null);
      setFormData({
        nome: '',
        descricao: '',
        cor: '#3b82f6',
        icone: 'Shield',
        nivel_acesso: 1,
        permissoes: [],
        is_active: true
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingFuncao(null);
    setFormData({
      nome: '',
      descricao: '',
      cor: '#3b82f6',
      icone: 'Shield',
      nivel_acesso: 1,
      permissoes: [],
      is_active: true
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.nome.trim()) {
      errors.nome = 'Nome é obrigatório';
    }

    if (formData.nivel_acesso < 1 || formData.nivel_acesso > 10) {
      errors.nivel_acesso = 'Nível deve estar entre 1 e 10';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitLoading(true);

    try {
      let result;
      
      if (editingFuncao) {
        result = await updateFuncao(editingFuncao.id, formData);
      } else {
        result = await createFuncao(formData);
      }

      if (result.success) {
        closeModal();
        loadData();
        alert(editingFuncao ? 'Função atualizada com sucesso!' : 'Função criada com sucesso!');
      } else {
        setFormErrors({ submit: result.error });
      }
    } catch (error) {
      setFormErrors({ submit: 'Erro interno. Tente novamente.' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleToggleStatus = async (funcao) => {
    try {
      const result = await toggleFuncaoStatus(funcao.id, !funcao.is_active);
      if (result.success) {
        loadData();
        alert(`Função ${!funcao.is_active ? 'ativada' : 'desativada'} com sucesso!`);
      } else {
        alert('Erro ao alterar status: ' + result.error);
      }
    } catch (error) {
      alert('Erro interno ao alterar status');
    }
  };

  const handleDelete = async (funcao) => {
    if (!window.confirm(`Tem certeza que deseja excluir a função "${funcao.nome}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const result = await deleteFuncao(funcao.id);
      if (result.success) {
        loadData();
        alert('Função excluída com sucesso!');
      } else {
        alert('Erro ao excluir função: ' + result.error);
      }
    } catch (error) {
      alert('Erro interno ao excluir função');
    }
  };

  const handleDuplicate = async (funcao) => {
    try {
      const result = await duplicateFuncao(funcao.id);
      if (result.success) {
        loadData();
        alert('Função duplicada com sucesso!');
      } else {
        alert('Erro ao duplicar função: ' + result.error);
      }
    } catch (error) {
      alert('Erro interno ao duplicar função');
    }
  };

  const handleViewUsers = async (funcao) => {
    try {
      const result = await getUsersByFuncao(funcao.id);
      if (result.success) {
        setSelectedFuncaoUsers(result.data);
        setShowUsersModal(true);
      } else {
        alert('Erro ao buscar usuários: ' + result.error);
      }
    } catch (error) {
      alert('Erro interno ao buscar usuários');
    }
  };

  const handlePermissionToggle = (permissionKey) => {
    const currentPermissions = formData.permissoes || [];
    const newPermissions = currentPermissions.includes(permissionKey)
      ? currentPermissions.filter(p => p !== permissionKey)
      : [...currentPermissions, permissionKey];
    
    setFormData({ ...formData, permissoes: newPermissions });
  };

  const getIconComponent = (iconName) => {
    const IconComponent = iconMap[iconName] || Shield;
    return IconComponent;
  };

  if (loading) {
    return (
      <div className="funcoes-usuarios">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando funções de usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="funcoes-usuarios">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1 className="page-title">Funções de Usuários</h1>
            <p className="page-subtitle">Gerenciamento de papéis e permissões do sistema</p>
          </div>
          <button className="btn-primary" onClick={() => openModal()}>
            <Plus size={20} />
            Nova Função
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon">
              <UserCheck size={24} />
            </div>
          </div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total de Funções</div>
        </div>

        <div className="stat-card success">
          <div className="stat-header">
            <div className="stat-icon success">
              <Users size={24} />
            </div>
          </div>
          <div className="stat-value">{stats.total_users}</div>
          <div className="stat-label">Usuários Atribuídos</div>
        </div>

        <div className="stat-card warning">
          <div className="stat-header">
            <div className="stat-icon warning">
              <Crown size={24} />
            </div>
          </div>
          <div className="stat-value">{stats.system}</div>
          <div className="stat-label">Funções do Sistema</div>
        </div>

        <div className="stat-card info">
          <div className="stat-header">
            <div className="stat-icon info">
              <Settings size={24} />
            </div>
          </div>
          <div className="stat-value">{stats.custom}</div>
          <div className="stat-label">Funções Customizadas</div>
        </div>
      </div>

      <div className="content-card">
        <div className="table-header">
          <div className="search-container">
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar por nome ou descrição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Função</th>
                <th>Descrição</th>
                <th>Nível</th>
                <th>Usuários</th>
                <th>Permissões</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {funcoes.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">
                    {searchTerm ? 'Nenhuma função encontrada com os filtros aplicados' : 'Nenhuma função cadastrada'}
                  </td>
                </tr>
              ) : (
                funcoes.map(funcao => {
                  const IconComponent = getIconComponent(funcao.icone);
                  return (
                    <tr key={funcao.id}>
                      <td>
                        <div className="role-info">
                          <div 
                            className="role-icon" 
                            style={{ backgroundColor: funcao.cor }}
                          >
                            <IconComponent size={16} />
                          </div>
                          <div>
                            <div className="role-name">{funcao.nome}</div>
                            {funcao.is_system && <span className="system-badge">Sistema</span>}
                          </div>
                        </div>
                      </td>
                      <td className="description-cell">{funcao.descricao}</td>
                      <td>
                        <div className="level-indicator">
                          <div className="level-bar">
                            <div 
                              className="level-fill" 
                              style={{ width: `${funcao.nivel_acesso * 10}%` }}
                            ></div>
                          </div>
                          <span className="level-text">{funcao.nivel_acesso}/10</span>
                        </div>
                      </td>
                      <td>
                        <button 
                          className="count-badge clickable"
                          onClick={() => handleViewUsers(funcao)}
                          title="Ver usuários"
                        >
                          {funcao.usuarios_count || 0}
                        </button>
                      </td>
                      <td>
                        <span className="count-badge">
                          {funcao.permissoes?.length || 0}
                        </span>
                      </td>
                      <td>
                        <button
                          className={`status-toggle ${funcao.is_active ? 'active' : 'inactive'}`}
                          onClick={() => handleToggleStatus(funcao)}
                          title={`Clique para ${funcao.is_active ? 'desativar' : 'ativar'}`}
                        >
                          {funcao.is_active ? 'Ativo' : 'Inativo'}
                        </button>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-action view"
                            onClick={() => handleViewUsers(funcao)}
                            title="Ver usuários"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="btn-action copy"
                            onClick={() => handleDuplicate(funcao)}
                            title="Duplicar função"
                          >
                            <Copy size={16} />
                          </button>
                          <button
                            className="btn-action edit"
                            onClick={() => openModal(funcao)}
                            title="Editar função"
                          >
                            <Edit2 size={16} />
                          </button>
                          {!funcao.is_system && (
                            <button
                              className="btn-action delete"
                              onClick={() => handleDelete(funcao)}
                              title="Excluir função"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Formulário */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingFuncao ? 'Editar Função' : 'Nova Função de Usuário'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="nome">Nome da Função *</label>
                  <input
                    type="text"
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    className={formErrors.nome ? 'error' : ''}
                    placeholder="Ex: Professor, Coordenador..."
                  />
                  {formErrors.nome && <span className="error-message">{formErrors.nome}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="nivel_acesso">Nível de Acesso (1-10) *</label>
                  <input
                    type="number"
                    id="nivel_acesso"
                    min="1"
                    max="10"
                    value={formData.nivel_acesso}
                    onChange={(e) => setFormData({...formData, nivel_acesso: parseInt(e.target.value)})}
                    className={formErrors.nivel_acesso ? 'error' : ''}
                  />
                  {formErrors.nivel_acesso && <span className="error-message">{formErrors.nivel_acesso}</span>}
                </div>

                <div className="form-group full-width">
                  <label htmlFor="descricao">Descrição</label>
                  <textarea
                    id="descricao"
                    value={formData.descricao}
                    onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                    placeholder="Descreva as responsabilidades desta função..."
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="cor">Cor de Identificação</label>
                  <div className="color-input">
                    <input
                      type="color"
                      id="cor"
                      value={formData.cor}
                      onChange={(e) => setFormData({...formData, cor: e.target.value})}
                    />
                    <Palette size={16} />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="icone">Ícone</label>
                  <select
                    id="icone"
                    value={formData.icone}
                    onChange={(e) => setFormData({...formData, icone: e.target.value})}
                  >
                    {getIconesDisponiveis().map(icon => {
                      const IconComponent = getIconComponent(icon);
                      return (
                        <option key={icon} value={icon}>{icon}</option>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  />
                  <span className="checkmark"></span>
                  Função ativa
                </label>
              </div>

              <div className="permissions-section">
                <div className="section-header">
                  <h3>Permissões</h3>
                  <button
                    type="button"
                    className="btn-secondary small"
                    onClick={() => setShowPermissionsModal(true)}
                  >
                    Gerenciar Permissões
                  </button>
                </div>
                <div className="permissions-summary">
                  {formData.permissoes?.length > 0 ? (
                    <p>{formData.permissoes.length} permissão(ões) selecionada(s)</p>
                  ) : (
                    <p>Nenhuma permissão selecionada</p>
                  )}
                </div>
              </div>

              {formErrors.submit && (
                <div className="error-message submit-error">{formErrors.submit}</div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={submitLoading}>
                  {submitLoading ? 'Salvando...' : (editingFuncao ? 'Atualizar' : 'Criar Função')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Usuários */}
      {showUsersModal && (
        <div className="modal-overlay" onClick={() => setShowUsersModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Usuários com esta Função</h2>
              <button className="modal-close" onClick={() => setShowUsersModal(false)}>×</button>
            </div>
            <div className="users-list">
              {selectedFuncaoUsers.length === 0 ? (
                <p className="no-users">Nenhum usuário atribuído a esta função</p>
              ) : (
                selectedFuncaoUsers.map(user => (
                  <div key={user.id} className="user-item">
                    <div className="user-info">
                      <strong>{user.name}</strong>
                      <span>{user.email}</span>
                      <small>{user.department}</small>
                    </div>
                    <span className={`user-status ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Permissões */}
      {showPermissionsModal && (
        <div className="modal-overlay" onClick={() => setShowPermissionsModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Gerenciar Permissões</h2>
              <button className="modal-close" onClick={() => setShowPermissionsModal(false)}>×</button>
            </div>
            <div className="permissions-manager">
              {Object.entries(
                getPermissoesDisponiveis().reduce((acc, perm) => {
                  if (!acc[perm.category]) acc[perm.category] = [];
                  acc[perm.category].push(perm);
                  return acc;
                }, {})
              ).map(([category, permissions]) => (
                <div key={category} className="permission-category">
                  <h4>{category}</h4>
                  <div className="permission-list">
                    {permissions.map(permission => (
                      <label key={permission.key} className="permission-item">
                        <input
                          type="checkbox"
                          checked={formData.permissoes?.includes(permission.key) || false}
                          onChange={() => handlePermissionToggle(permission.key)}
                        />
                        <span className="checkmark"></span>
                        <div className="permission-info">
                          <strong>{permission.label}</strong>
                          <small>{permission.key}</small>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button 
                className="btn-primary" 
                onClick={() => setShowPermissionsModal(false)}
              >
                Concluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FuncoesUsuarios;
